import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import {
  LayoutDashboard,
  Users,
  Building2,
  ClipboardList,
  ClipboardCheck,
  Calendar,
  FileText,
  FileCheck,
  Settings,
  LogOut,
  Menu,
  X,
  Leaf,
  ChevronRight,
  Receipt,
  Bell,
  MessageCircle,
  Calculator,
  ListChecks,
  Award,
  BookOpen,
  UserCheck,
  BarChart3,
  Shield,
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import MessageBell from "./MessageBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const { user, clearAllCaches } = useContext(AuthContext);
  const { getTenantLogo, getTenantName, isSuperAdminView, tenantSlug } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [allowedModules, setAllowedModules] = useState(null); // null = loading, [] = loaded
  const [showGuideModal, setShowGuideModal] = useState(false);

  // Role-based guide content - DETAILED
  const guides = {
    planning_expert: {
      title: "Planlama Uzmanı Kılavuzu",
      icon: <ClipboardList className="w-6 h-6" />,
      sections: [
        {
          title: "1. Yeni Müşteri Ekleme",
          steps: [
            "Sol menüden 'Müşteriler' sekmesine tıklayın",
            "Sağ üstteki yeşil 'Yeni Müşteri' butonuna tıklayın",
            "Açılan formda zorunlu alanları doldurun:",
            "  • Tesis Adı: Müşterinin tesis/otel adı",
            "  • Ticari Ünvan: Resmi firma adı",
            "  • İl/İlçe: Tesisin bulunduğu konum",
            "  • Telefon ve E-posta: İletişim bilgileri",
            "  • Oda Sayısı, Yatak Sayısı, Çalışan Sayısı: Adam-gün hesabı için gerekli",
            "  • Denetim Aşaması: 2. Aşama (TR-I) veya 3. Aşama (GSTC) seçin",
            "'Kaydet' butonuna tıklayarak müşteriyi oluşturun"
          ]
        },
        {
          title: "2. Adam-Gün Hesabı Yapma",
          steps: [
            "Sol menüden 'Adam Gün Hesabı' sekmesine gidin",
            "Üstteki dropdown'dan müşteri seçin (Sadece 3. Aşama müşterileri görünür)",
            "Risk faktörlerini işaretleyin:",
            "  • Karmaşık süreçler varsa ilgili kutuyu işaretleyin",
            "  • Çoklu lokasyon, mevsimsel işçi vb. faktörleri belirtin",
            "Sistem otomatik olarak toplam adam-gün süresini hesaplar",
            "'Hesaplamayı Kaydet' butonuna tıklayın",
            "Onaylayan ve Gözden Geçiren kişileri seçin",
            "'FR.02 İndir' butonu ile başvuru formunu indirin"
          ]
        },
        {
          title: "3. Denetim Planlama",
          steps: [
            "Sol menüden 'Denetimler' sekmesine gidin",
            "'Yeni Denetim' butonuna tıklayın",
            "Açılan formda:",
            "  • Müşteri: Dropdown'dan müşteri seçin",
            "  • Denetim Türü: 2. Aşama veya 3. Aşama seçin",
            "  • Başlangıç/Bitiş Tarihi: Takvimden tarih seçin",
            "  • Denetçi: Atanacak denetçiyi seçin",
            "  • Adam/Gün: 3. Aşama için otomatik gelir, 2. Aşama için manuel girin",
            "'Kaydet' ile denetimi oluşturun",
            "Denetçiye otomatik bildirim gönderilir"
          ]
        },
        {
          title: "4. Toplu Müşteri İçe Aktarma (Excel)",
          steps: [
            "Müşteriler sayfasında 'Şablon İndir' butonuna tıklayın",
            "İndirilen Excel dosyasını açın",
            "Örnek satırları inceleyerek kendi verilerinizi girin",
            "Her satır bir müşteri kaydıdır",
            "Zorunlu alan: Tesis Adı (B sütunu)",
            "Denetim Aşaması (AL sütunu): 2 veya 3 yazın",
            "Dosyayı kaydedin",
            "'Excel'den İçe Aktar' butonuna tıklayın",
            "Dosyayı seçin ve yükleyin",
            "Sistem verileri kontrol edip içe aktarır"
          ]
        },
        {
          title: "5. Takvim Görünümü",
          steps: [
            "Sol menüden 'Takvim' sekmesine gidin",
            "Planlanmış denetimleri takvim üzerinde görün",
            "Renkler durumu gösterir: Yeşil=Tamamlandı, Mavi=Planlandı",
            "Bir denetime tıklayarak detaylarına gidin"
          ]
        }
      ]
    },
    auditor: {
      title: "Denetçi Kılavuzu",
      icon: <UserCheck className="w-6 h-6" />,
      sections: [
        {
          title: "1. Size Atanan Denetimleri Görme",
          steps: [
            "Giriş yaptığınızda Dashboard'da atanan denetimlerinizi görürsünüz",
            "Sol menüden 'Denetimler' sekmesine tıklayın",
            "Listede sadece size atanan denetimler görünür",
            "Denetim satırına tıklayarak detayları görün",
            "Müşteri adı, tarih, durum bilgilerini kontrol edin"
          ]
        },
        {
          title: "2. Denetim Raporu Oluşturma",
          steps: [
            "'Raporlar' sekmesine gidin",
            "'Yeni Rapor' butonuna tıklayın",
            "Açılan pencereden denetimi seçin",
            "Rapor formu açılır - 7 sekme bulunur:",
            "  • Checklist: GSTC/TR-I kriterlerini değerlendirin",
            "  • Rapor (FR.10): Genel değerlendirme notları",
            "  • Görüşmeler: Mülakat yapılan kişiler",
            "  • Toplantılar: Açılış/kapanış toplantı katılımcıları",
            "  • Kanıtlar: Fotoğraf ve belge linkleri",
            "  • Uygunsuzluklar: Minör/Majör bulgular",
            "  • Belgeler: FR formları indirme"
          ]
        },
        {
          title: "3. Checklist Doldurma (GSTC Kriterleri)",
          steps: [
            "'Checklist' sekmesine gidin",
            "Her kriter için bir değerlendirme seçin:",
            "  • Uygun (C): Kriter karşılanıyor",
            "  • Minör (MI): Küçük eksiklik var",
            "  • Majör (MA): Ciddi uygunsuzluk",
            "  • Gözlem (OB): İyileştirme önerisi",
            "  • N/A: Kriter uygulanamaz",
            "'Kanıt' alanına objektif delili yazın",
            "'Notlar' alanına açıklamalarınızı girin",
            "Üstteki 'Kaydet' butonuna düzenli olarak tıklayın"
          ]
        },
        {
          title: "4. Fotoğraf ve Kanıt Ekleme",
          steps: [
            "'Kanıtlar' sekmesine gidin",
            "'Fotoğraf Ekle' butonuna tıklayın",
            "Telefonunuzdan veya bilgisayarınızdan fotoğraf seçin",
            "Fotoğraf otomatik yüklenir",
            "WeTransfer/Google Drive linki eklemek için:",
            "  • Link türünü seçin",
            "  • URL'yi yapıştırın",
            "  • Açıklama yazın",
            "  • 'Ekle' butonuna tıklayın"
          ]
        },
        {
          title: "5. Görüşme ve Toplantı Bilgileri",
          steps: [
            "'Görüşmeler' sekmesinde mülakat yaptığınız kişileri ekleyin:",
            "  • Kişi adı, pozisyonu, departmanı girin",
            "  • 'Ekle' butonuna tıklayın",
            "'Toplantılar' sekmesinde açılış/kapanış toplantı bilgilerini girin:",
            "  • Toplantı tarihi ve saati",
            "  • Katılımcı listesi (isim, pozisyon)"
          ]
        },
        {
          title: "6. Raporu Onaya Gönderme",
          steps: [
            "Tüm kriterleri değerlendirdiğinizden emin olun",
            "Eksik kriter varsa sistem uyarı verir",
            "Rapor sayfasının üstündeki 'Onaya Gönder' butonuna tıklayın",
            "Onay penceresi açılır - 'Gönder' ile onaylayın",
            "Rapor durumu 'İncelemede' olarak değişir",
            "Teknik Yönetici'ye bildirim gönderilir"
          ]
        },
        {
          title: "7. Belgeleri İndirme",
          steps: [
            "'Belgeler' sekmesine gidin",
            "İndirmek istediğiniz belgeye tıklayın:",
            "  • FR.10 Denetim Raporu (Word/PDF)",
            "  • FR.50 Checklist",
            "  • FR.45 Toplantı Tutanağı",
            "  • FR.42 Denetim Planı",
            "  • FR.44 Ekip Atama Formu",
            "Belgeler otomatik olarak müşteri bilgileriyle doldurulur"
          ]
        }
      ]
    },
    technical_manager: {
      title: "Teknik Yönetici Kılavuzu",
      icon: <BarChart3 className="w-6 h-6" />,
      sections: [
        {
          title: "1. Onay Bekleyen Raporları Görme",
          steps: [
            "Sol menüden 'Rapor Onay' sekmesine gidin",
            "Onay bekleyen raporlar listelenir",
            "Durum sütununda 'İncelemede' yazan raporları görün",
            "Denetçi adı, müşteri ve tarih bilgilerini kontrol edin"
          ]
        },
        {
          title: "2. Rapor İnceleme ve Değerlendirme",
          steps: [
            "İncelemek istediğiniz rapora tıklayın",
            "Rapor detay sayfası açılır",
            "Checklist sekmesinde tüm kriterleri gözden geçirin:",
            "  • Her kriterin değerlendirmesini kontrol edin",
            "  • Kanıt ve notları okuyun",
            "  • Fotoğrafları inceleyin",
            "Uygunsuzluklar sekmesinde minör/majör bulguları kontrol edin",
            "Görüşme ve toplantı bilgilerinin eksiksiz olduğunu doğrulayın"
          ]
        },
        {
          title: "3. Raporu Onaylama",
          steps: [
            "Rapor uygunsa sağ üstteki 'Onayla' butonuna tıklayın",
            "Onay penceresi açılır",
            "İsteğe bağlı yorum yazabilirsiniz",
            "'Onayla' butonuna tıklayın",
            "Rapor durumu 'Onaylandı' olarak değişir",
            "Denetçiye bildirim gönderilir"
          ]
        },
        {
          title: "4. Raporu Reddetme (Geri Gönderme)",
          steps: [
            "Eksiklikler varsa 'Reddet' butonuna tıklayın",
            "Açılan pencerede red gerekçesini yazın:",
            "  • Hangi kriterlerde eksiklik var?",
            "  • Ne düzeltilmeli?",
            "'Reddet' butonuna tıklayın",
            "Rapor denetçiye geri gönderilir",
            "Denetçi düzeltme yapıp tekrar gönderebilir"
          ]
        },
        {
          title: "5. FR.48 Karar Formu Doldurma",
          steps: [
            "Sol menüden 'FR.48 Karar' sekmesine gidin",
            "Üstteki dropdown'dan onaylanmış denetimi seçin",
            "Form otomatik olarak müşteri bilgileriyle dolar",
            "Teknik İnceleme bölümünü doldurun:",
            "  • Her soru için Evet/Hayır seçin",
            "  • Hayır ise gerekçe yazın",
            "Komite Kriterleri bölümünü doldurun",
            "Denetçi Performansı değerlendirmesini yapın",
            "Sertifika Kararını seçin: Kabul/Red/Askıya Alma",
            "'Kaydet' butonuna tıklayın",
            "'FR.48 İndir' ile formu indirin"
          ]
        }
      ]
    },
    admin: {
      title: "Admin Kılavuzu",
      icon: <Shield className="w-6 h-6" />,
      sections: [
        {
          title: "1. Yeni Kullanıcı Ekleme",
          steps: [
            "Sol menüden 'Kullanıcılar' sekmesine gidin",
            "'Yeni Kullanıcı' butonuna tıklayın",
            "Kullanıcı bilgilerini girin:",
            "  • Ad Soyad: Kullanıcının tam adı",
            "  • E-posta: Giriş için kullanılacak e-posta",
            "  • Şifre: Güçlü bir şifre belirleyin",
            "  • Rol: Dropdown'dan seçin",
            "     - Planlama Uzmanı: Müşteri ve denetim planlama",
            "     - Denetçi: Denetim yapma ve raporlama",
            "     - Teknik Yönetici: Rapor onaylama",
            "     - Satış Temsilcisi: Müşteri takibi",
            "     - Sertifika Sorumlusu: Sertifika yönetimi",
            "'Kaydet' butonuna tıklayın"
          ]
        },
        {
          title: "2. Kullanıcı Düzenleme/Silme",
          steps: [
            "Kullanıcılar listesinde düzenlemek istediğiniz kullanıcıyı bulun",
            "Satırın sağındaki kalem ikonuna tıklayın",
            "Bilgileri güncelleyin ve 'Kaydet' deyin",
            "Silmek için çöp kutusu ikonuna tıklayın",
            "Onay penceresinde 'Sil' butonuna tıklayın"
          ]
        },
        {
          title: "3. Sertifika Oluşturma",
          steps: [
            "Sol menüden 'Sertifikalar' sekmesine gidin",
            "'Yeni Sertifika' butonuna tıklayın",
            "Müşteriyi ve ilgili denetimi seçin",
            "Sertifika bilgilerini girin:",
            "  • Sertifika No: Benzersiz numara",
            "  • Düzenleme Tarihi: Bugünün tarihi",
            "  • Geçerlilik Tarihi: 3 yıl sonrası",
            "  • Kapsam: GSTC Industry Criteria",
            "'Oluştur' butonuna tıklayın",
            "Sertifika PDF olarak oluşturulur",
            "QR kod otomatik eklenir"
          ]
        },
        {
          title: "4. Sistem Ayarları",
          steps: [
            "Sol menüden 'Ayarlar' sekmesine gidin",
            "Firma Bilgileri bölümünde:",
            "  • Firma adı, logo, renk teması ayarlayın",
            "Modül Erişim Ayarları bölümünde:",
            "  • Hangi rollerin hangi modüllere erişeceğini belirleyin",
            "  • Checkbox'ları işaretleyin/kaldırın",
            "'Kaydet' butonuna tıklayın"
          ]
        }
      ]
    },
    super_admin: {
      title: "Süper Admin Kılavuzu",
      icon: <Shield className="w-6 h-6" />,
      sections: [
        {
          title: "1. Tam Yetki Erişimi",
          steps: [
            "Süper Admin olarak tüm modüllere erişiminiz var",
            "Tüm müşterileri, denetimleri ve raporları görebilirsiniz",
            "Tüm kullanıcıları yönetebilirsiniz",
            "Sistem ayarlarını değiştirebilirsiniz"
          ]
        },
        {
          title: "2. Dashboard İstatistikleri",
          steps: [
            "Dashboard'da genel durumu görün:",
            "  • Toplam müşteri sayısı",
            "  • Aktif denetim sayısı",
            "  • Onay bekleyen rapor sayısı",
            "  • Geçerli sertifika sayısı",
            "Grafiklerde aylık denetim trendlerini takip edin"
          ]
        },
        {
          title: "3. Tüm İşlemleri Görüntüleme",
          steps: [
            "Her modülde tüm kayıtları görebilirsiniz",
            "Filtreleri kullanarak arama yapın",
            "Excel'e aktararak raporlar oluşturun"
          ]
        }
      ]
    },
    sales_representative: {
      title: "Satış Temsilcisi Kılavuzu",
      icon: <Users className="w-6 h-6" />,
      sections: [
        {
          title: "1. Müşteri Listesi ve Takip",
          steps: [
            "Sol menüden 'Müşteriler' sekmesine gidin",
            "Potansiyel ve mevcut müşterileri görün",
            "Arama kutusundan müşteri arayın",
            "Filtrelerle duruma göre listeleyin"
          ]
        },
        {
          title: "2. Müşteri Bilgilerini Güncelleme",
          steps: [
            "Müşteri satırına tıklayarak detaya gidin",
            "Satış bilgilerini güncelleyin:",
            "  • Satış Temsilcisi: Kendi adınızı seçin",
            "  • Satış Tutarı: Teklif tutarı",
            "  • Ödeme Durumu: Bekliyor/Kısmi/Tamamlandı",
            "  • Aksiyon: Teklif Gönderildi/Sözleşme İmzalandı vb.",
            "  • Açıklama: Notlarınızı yazın",
            "'Kaydet' butonuna tıklayın"
          ]
        },
        {
          title: "3. Satış Takibi",
          steps: [
            "Müşteri kartında ödeme sözü tarihini girin",
            "Vadesi gelen ödemeleri takip edin",
            "Bildirimlerden hatırlatmaları görün"
          ]
        }
      ]
    },
    certificate_responsible: {
      title: "Sertifika Sorumlusu Kılavuzu",
      icon: <Award className="w-6 h-6" />,
      sections: [
        {
          title: "1. Sertifika Listesini Görme",
          steps: [
            "Sol menüden 'Sertifikalar' sekmesine gidin",
            "Tüm sertifikaları listede görün",
            "Durum sütununda: Geçerli/Askıda/İptal",
            "Geçerlilik tarihini kontrol edin"
          ]
        },
        {
          title: "2. Yeni Sertifika Oluşturma",
          steps: [
            "'Yeni Sertifika' butonuna tıklayın",
            "Müşteriyi dropdown'dan seçin",
            "Onaylanmış denetimi seçin",
            "Sertifika bilgilerini doldurun:",
            "  • Sertifika No: RC-YYYY-XXX formatında",
            "  • GSTC Kodu: (varsa)",
            "  • Düzenleme Tarihi: Bugün",
            "  • Geçerlilik: 3 yıl sonra",
            "'Oluştur' butonuna tıklayın"
          ]
        },
        {
          title: "3. Sertifika İndirme ve Gönderme",
          steps: [
            "Sertifika oluştuktan sonra 'PDF İndir' butonuna tıklayın",
            "QR kodlu sertifika indirilir",
            "QR kod tarandığında doğrulama sayfası açılır",
            "'Müşteriye Gönder' butonu ile e-posta gönderin"
          ]
        },
        {
          title: "4. Sertifika Durumu Güncelleme",
          steps: [
            "Sertifika satırındaki 'Düzenle' butonuna tıklayın",
            "Durumu değiştirin: Geçerli/Askıda/İptal",
            "Askıya alma veya iptal için gerekçe yazın",
            "'Kaydet' butonuna tıklayın"
          ]
        }
      ]
    }
  };

  // Get current user's guide based on primary role (first role or role field)
  const getPrimaryRole = () => {
    if (!user) return 'auditor';
    if (user.roles && user.roles.length > 0) return user.roles[0];
    return user.role || 'auditor';
  };
  const currentGuide = guides[getPrimaryRole()] || guides.auditor;

  // Get role label for display - show all roles if multiple
  const getUserRoleDisplay = () => {
    if (!user) return '';
    const userRoles = user.roles || [user.role];
    if (userRoles.length === 1) {
      return getRoleLabel(userRoles[0]);
    }
    // Show first role + count of others
    return `${getRoleLabel(userRoles[0])} +${userRoles.length - 1}`;
  };

  // Fetch module permissions from backend
  useEffect(() => {
    const fetchModulePermissions = async () => {
      try {
        const response = await axios.get(`${API}/module-permissions`, { withCredentials: true });
        setAllowedModules(response.data.modules || []);
      } catch (error) {
        // If error, use null to fall back to default role-based filtering
        setAllowedModules(null);
      }
    };

    if (user) {
      fetchModulePermissions();
    }
  }, [user]);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/messages/unread-count`, { withCredentials: true });
        setUnreadMessages(response.data.unread_count || 0);
      } catch (error) {
        // Ignore errors
      }
    };

    if (user) {
      fetchUnreadCount();
      // Poll every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      // Clear all caches first
      if (clearAllCaches) {
        await clearAllCaches();
      }
      // Clear cached user data
      localStorage.removeItem('cachedUser');
      // Then call logout API
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/", { replace: true });
    } catch (error) {
      // Clear cached user even on error
      localStorage.removeItem('cachedUser');
      toast.error("Çıkış yapılırken hata oluştu");
      // Still navigate to home even if error
      navigate("/", { replace: true });
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: "Süper Admin",
      admin: "Admin",
      technical_manager: "Teknik Yönetici",
      planning_expert: "Planlama Uzmanı",
      auditor: "Denetçi",
      sales_representative: "Satış Temsilcisi",
      certificate_responsible: "Sertifika Sorumlusu",
      technical_reviewer: "Teknik İncelemeci",
      certification_committee: "Belgelendirme Komitesi"
    };
    return labels[role] || role;
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "technical_manager", "planning_expert", "auditor", "super_admin", "certificate_responsible", "technical_reviewer", "certification_committee"] },
    { path: "/customers", icon: Building2, label: "Müşteriler", roles: ["admin", "planning_expert", "super_admin", "sales_representative", "certificate_responsible"] },
    // RoyalCert Only: Adam-Gün Hesaplama - Müşteriler ile Denetimler arasında
    ...(tenantSlug === 'royalcert' ? [
      { path: "/manday-calculator", icon: Calculator, label: "Adam-Gün Hesaplama", roles: ["admin", "planning_expert", "super_admin", "sales_representative"] }
    ] : []),
    { path: "/audits", icon: ClipboardList, label: "Denetimler", roles: ["admin", "planning_expert", "auditor", "super_admin"] },
    { path: "/calendar", icon: Calendar, label: "Takvim", roles: ["admin", "planning_expert", "auditor", "super_admin"] },
    { path: "/reports", icon: FileText, label: "Raporlar", roles: ["admin", "technical_manager", "auditor", "super_admin"] },
    { path: "/reports/review", icon: FileCheck, label: "Rapor Onay", roles: ["admin", "technical_manager", "super_admin"] },
    // RoyalCert Only: Sertifikalar ve FR.48
    ...(tenantSlug === 'royalcert' ? [
      { path: "/fr48-decisions", icon: ClipboardCheck, label: "FR.48 Karar", roles: ["admin", "super_admin", "certificate_responsible", "technical_reviewer", "certification_committee"] },
      { path: "/certificates", icon: Award, label: "Sertifikalar", roles: ["admin", "super_admin", "certificate_responsible", "certification_committee"] }
    ] : []),
    { path: "/masraflar", icon: Receipt, label: "Masraflar", roles: ["admin", "technical_manager", "planning_expert", "auditor", "super_admin"] },
    { path: "/mesajlar", icon: MessageCircle, label: "Mesajlar", roles: ["admin", "technical_manager", "planning_expert", "auditor", "super_admin", "sales_representative", "certificate_responsible", "technical_reviewer", "certification_committee"] },
    { path: "/bildirimler", icon: Bell, label: "Bildirimler", roles: ["admin", "technical_manager", "planning_expert", "auditor", "super_admin", "sales_representative", "certificate_responsible", "technical_reviewer", "certification_committee"] },
    { path: "/users", icon: Users, label: "Kullanıcılar", roles: ["admin", "super_admin"] },
    { path: "/checklist-templates", icon: ListChecks, label: "Kontrol Listeleri", roles: ["super_admin"] },
    { path: "/ayarlar", icon: Settings, label: "Ayarlar", roles: ["admin", "technical_manager", "planning_expert", "auditor", "super_admin", "sales_representative", "certificate_responsible", "technical_reviewer", "certification_committee"] },
  ];

  // Helper function to check if user has any of the specified roles
  const userHasAnyRole = (checkRoles) => {
    if (!user) return false;
    const userRoles = user.roles || [user.role];
    return checkRoles.some(role => userRoles.includes(role));
  };

  // Filter nav items based on tenant-specific module permissions or fallback to role-based
  const filteredNavItems = navItems.filter(item => {
    // Super admin always has access to everything
    if (user?.role === "super_admin" || user?.roles?.includes("super_admin")) {
      return true;
    }
    
    // If we have tenant-specific module permissions loaded, use ONLY those
    // This allows admins to grant access to modules not in the default role permissions
    if (allowedModules !== null && allowedModules.length > 0) {
      return allowedModules.includes(item.path);
    }
    
    // Fallback: use role-based permissions - check ALL user roles
    return userHasAnyRole(item.roles);
  });

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const showBadge = item.path === "/mesajlar" && unreadMessages > 0;
    
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setSidebarOpen(false)}
        data-testid={`nav-${item.path.replace("/", "")}`}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
          isActive
            ? "bg-[#1A4D2E] text-white shadow-md"
            : "text-[#4B5563] hover:bg-[#E8F3EE] hover:text-[#1A4D2E]"
        }`}
      >
        <item.icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
        {showBadge && (
          <span className={`ml-auto px-2 py-0.5 text-xs rounded-full ${
            isActive ? 'bg-white text-[#1A4D2E]' : 'bg-[#1A4D2E] text-white'
          }`}>
            {unreadMessages > 9 ? '9+' : unreadMessages}
          </span>
        )}
        {isActive && !showBadge && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E8F3EE] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-[#E8F3EE] transition-colors"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6 text-[#1A4D2E]" />
            </button>
            <div className="flex items-center gap-2">
              <img src={getTenantLogo()} alt="Logo" className="w-8 h-8 object-contain" />
              <span className="font-semibold text-[#1A4D2E] text-sm truncate max-w-[150px]">{getTenantName()}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MessageBell />
            <NotificationBell />
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#1A4D2E] text-white text-xs">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-white border-r border-[#E8F3EE] z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-6 py-6 border-b border-[#E8F3EE]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={getTenantLogo()} alt="Logo" className="w-12 h-12 object-contain" />
                <div>
                  <h1 className="font-bold text-[#1A4D2E] tracking-tight text-sm">{getTenantName()}</h1>
                  <p className="text-xs text-[#9CA3AF]">BELGELENDİRME</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-lg hover:bg-[#E8F3EE]"
              >
                <X className="w-5 h-5 text-[#4B5563]" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => (
              <NavLink key={item.path} item={item} mobile />
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-[#E8F3EE]">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#1A4D2E] text-white">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#111827] truncate">{user?.name}</p>
                <p className="text-xs text-[#9CA3AF] truncate" title={(user?.roles || [user?.role]).map(r => getRoleLabel(r)).join(', ')}>
                  {getUserRoleDisplay()}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-[#4B5563] hover:text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 bg-white border-b border-[#E8F3EE]">
          <div>
            <h2 className="text-lg font-semibold text-[#111827]">
              {filteredNavItems.find(item => item.path === location.pathname)?.label || "Dashboard"}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <MessageBell />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#E8F3EE] transition-colors" data-testid="user-dropdown">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#1A4D2E] text-white text-sm">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="text-sm font-medium text-[#111827]">{user?.name}</p>
                    <p className="text-xs text-[#9CA3AF]" title={(user?.roles || [user?.role]).map(r => getRoleLabel(r)).join(', ')}>
                      {getUserRoleDisplay()}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Hesabım</DropdownMenuLabel>
                {/* Show all user roles */}
                {(user?.roles?.length > 1 || (user?.roles && user?.roles[0] !== user?.role)) && (
                  <>
                    <div className="px-2 py-1.5">
                      <p className="text-xs text-gray-500 mb-1">Roller:</p>
                      <div className="flex flex-wrap gap-1">
                        {(user?.roles || [user?.role]).map((role, idx) => (
                          <span key={idx} className="px-2 py-0.5 text-xs bg-[#E8F3EE] text-[#1A4D2E] rounded">
                            {getRoleLabel(role)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/ayarlar")} className="text-[#4B5563] cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Ayarlar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowGuideModal(true)} className="text-[#4B5563] cursor-pointer">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Kullanım Kılavuzu
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="dropdown-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Usage Guide Modal */}
      <Dialog open={showGuideModal} onOpenChange={setShowGuideModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {currentGuide.icon}
              {currentGuide.title}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6">
              {currentGuide.sections.map((section, idx) => (
                <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold text-[#1A4D2E] mb-3">{section.title}</h3>
                  <ul className="space-y-2">
                    {section.steps.map((step, stepIdx) => (
                      <li key={stepIdx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-[#1A4D2E] font-medium min-w-[20px]">{stepIdx + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
